import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Box, Stepper, Button, CircularProgress } from "@mui/material";

import Question from "../Components/Questions";
import ProgressBar from "../Components/ProgressBar";

import prevArrow from "../Assets/Images/prev-arrow.svg";
import nextArrow from "../Assets/Images/next-arrow.svg";
import useWindowDimensions from "../Hooks/useWindowDimensions";
import "../App.scss";

// HARD_CODED_JUMPS schema:
// questionId: {
//   [numericAnswer]: targetQuestionId,
//   ["exact text answer"]: targetQuestionId,           // textarea exact match
//   ["regex:<pattern>"]: targetQuestionId,             // textarea regex match (case-insensitive)
//   _TEXT_NON_EMPTY: targetQuestionId,                  // any non-empty textarea
//   _ANY: targetQuestionId                              // fallback if nothing else matches
// }
const HARD_CODED_JUMPS = {
  // 123: { 1: 456, 2: 789, "Yes": 456, "regex:^no": 789, _ANY: 111 },
  2881: { 1: 2882, 2: 2883, 3: 2884, 4: 2885, 5: 2886 },
};

// Question types that are considered optional (not mandatory) for unanswered checks
const NON_MANDATORY_TYPES = ["textarea"];

// (Deprecated) answered check was replaced by path-aware blocking logic below

// During initial path reconstruction, decide if we should stop on this index
// Rule: Optional types generally don't block, BUT if the optional is empty
// and the next sequential question is required and unanswered, stop here.
const isBlockingAtIndexForPath = (questionList, index) => {
  const current = questionList?.[index];
  if (!current) return false;

  const isOptional = NON_MANDATORY_TYPES.includes(current.answerType);
  if (isOptional) {
    const hasNonEmptyText =
      typeof current.answerTxt === "string" &&
      current.answerTxt.trim().length > 0;
    if (!hasNonEmptyText) {
      const nextIndex = index + 1;
      const next =
        nextIndex < questionList.length ? questionList[nextIndex] : null;
      if (next && !NON_MANDATORY_TYPES.includes(next.answerType)) {
        const nextAnswered = Number(next.answer) > 0;
        if (!nextAnswered) return true; // don't skip current optional if next required is unanswered
      }
    }
    return false; // optional does not block otherwise
  }

  // Required blocks when unanswered
  return !(Number(current.answer) > 0);
};

// Helper to check if a question should be skipped based on jump mappings (static version)
const isSkippedQuestionStatic = (questionList, questionId) => {
  for (const parentIdStr of Object.keys(HARD_CODED_JUMPS)) {
    const parentId = Number(parentIdStr);
    const mapping = HARD_CODED_JUMPS[parentId];
    if (!mapping) continue;

    const parent = questionList.find((q) => q.id === parentId);
    if (parent && parent.answer) {
      const chosenTarget = mapping[parent.answer];
      const targets = Object.values(mapping);
      if (targets.includes(questionId) && questionId !== chosenTarget) {
        return true;
      }
    }
  }
  return false;
};

// Static resolver used during initialization (does not rely on component state)
const resolveJumpTargetStatic = (questionList, currentIndex) => {
  const currentQuestion = questionList?.[currentIndex];
  if (!currentQuestion) return null;

  const mapping = HARD_CODED_JUMPS[currentQuestion.id];
  if (mapping) {
    if (currentQuestion.answerType !== "textarea") {
      // Simplified: numeric/mcq/radio use mapping[answer]
      const targetQuestionId = mapping[currentQuestion.answer];
      if (targetQuestionId) {
        const idx = questionList.findIndex((q) => q.id === targetQuestionId);
        if (idx !== -1) return idx;
      }
    } else {
      // Simplified: textarea uses exact text or _TEXT_NON_EMPTY
      const text = (currentQuestion.answerTxt || "").trim();
      const targetQuestionId = text
        ? mapping[text] || mapping._TEXT_NON_EMPTY
        : null;
      if (targetQuestionId) {
        const idx = questionList.findIndex((q) => q.id === targetQuestionId);
        if (idx !== -1) return idx;
      }
    }
  }

  // Find next non-skipped question instead of just currentIndex + 1
  let nextIndex = currentIndex + 1;
  while (nextIndex < questionList.length) {
    const nextQuestion = questionList[nextIndex];
    if (
      nextQuestion &&
      !isSkippedQuestionStatic(questionList, nextQuestion.id)
    ) {
      return nextIndex;
    }
    nextIndex++;
  }

  return null;
};

// Build the path the user would have taken given existing answers and jump rules
const buildPathFromAnswers = (questionList) => {
  if (!questionList?.length) return { path: [0], current: 0 };
  const visited = new Set();
  const path = [];
  let currentIndex = 0;
  let iterations = 0;
  const maxIterations = questionList.length + 2;

  while (iterations < maxIterations && currentIndex != null) {
    if (visited.has(currentIndex)) break; // prevent loops
    visited.add(currentIndex);
    path.push(currentIndex);

    if (isBlockingAtIndexForPath(questionList, currentIndex)) break; // stop at first blocking (unanswered) in this path

    const nextIndex = resolveJumpTargetStatic(questionList, currentIndex);
    if (nextIndex == null) break; // end
    currentIndex = nextIndex;
    iterations += 1;
  }

  const current = path[path.length - 1] ?? 0;
  return { path, current };
};

const AssessmentV3 = () => {
  const { windowWidth } = useWindowDimensions();
  let navigate = useNavigate();
  const { id, pageId } = useParams();

  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);
  // Explicit navigation history (stack of question indices actually visited)
  const [navigationHistory, setNavigationHistory] = useState([]);
  // Record chosen branch per parent questionId { [parentId]: chosenTargetQuestionId }
  const [pathChoices, setPathChoices] = useState({});

  // Compute whether a question id is currently skipped due to a chosen branch
  const isSkippedQuestionId = (questionId) => {
    // Check all parents with jump mappings, not just those in pathChoices
    for (const parentIdStr of Object.keys(HARD_CODED_JUMPS)) {
      const parentId = Number(parentIdStr);
      const mapping = HARD_CODED_JUMPS[parentId];
      if (!mapping) continue;

      // Get chosen target from pathChoices, or infer from saved answer
      let chosenTarget = pathChoices[parentIdStr];
      if (!chosenTarget) {
        const parent = questions.find((q) => q.id === parentId);
        if (parent && parent.answer) {
          // Infer the chosen target from the parent's saved answer
          chosenTarget = mapping[parent.answer];
        }
      }

      const targets = Object.values(mapping);
      if (targets.includes(questionId)) {
        // If this question is a jump target but not the chosen one, skip it
        // If no choice yet and this is not the first target in sequence, skip it
        if (!chosenTarget) {
          const parentIndex = questions.findIndex((q) => q.id === parentId);
          const questionIndex = questions.findIndex((q) => q.id === questionId);
          // Only show targets that come after the parent in the sequence order
          if (
            parentIndex >= 0 &&
            questionIndex >= 0 &&
            questionIndex <= parentIndex
          ) {
            return true;
          }
          // For questions after the parent, only show the first target option
          const firstTargetId = Object.values(mapping)[0];
          if (questionId !== firstTargetId) {
            return true;
          }
        } else if (questionId !== chosenTarget) {
          return true;
        }
      }
    }
    return false;
  };

  const findNextNonSkippedIndex = (startIndex) => {
    let i = startIndex;
    while (i < questions.length) {
      const qid = questions[i]?.id;
      if (!qid || !isSkippedQuestionId(qid)) return i;
      i += 1;
    }
    return null;
  };

  // Resolve next target index using current answers or an override { answer, answerTxt }.
  const resolveJumpTarget = (currentIndex, override = {}) => {
    const currentQuestion = questions?.[currentIndex];
    if (!currentQuestion) return null;

    const questionId = currentQuestion.id;
    const mapping = HARD_CODED_JUMPS[questionId];

    if (mapping) {
      if (currentQuestion.answerType !== "textarea") {
        // Simplified numeric mapping (prefer fresh override)
        const effectiveAnswer =
          override.answer !== undefined
            ? override.answer
            : currentQuestion.answer;
        const targetQuestionId = mapping[effectiveAnswer];
        if (targetQuestionId) {
          const targetIndex = questions.findIndex(
            (q) => q.id === targetQuestionId
          );
          if (targetIndex !== -1) return targetIndex;
        }
      } else {
        // Simplified text mapping: exact or _TEXT_NON_EMPTY (prefer fresh override)
        const effectiveText = (
          (override.answerTxt !== undefined
            ? override.answerTxt
            : currentQuestion.answerTxt) || ""
        ).trim();
        const targetQuestionId = effectiveText
          ? mapping[effectiveText] || mapping._TEXT_NON_EMPTY
          : null;
        if (targetQuestionId) {
          const targetIndex = questions.findIndex(
            (q) => q.id === targetQuestionId
          );
          if (targetIndex !== -1) return targetIndex;
        }
      }
    }

    // Fallback to the next question sequentially, skipping any disallowed ids
    return findNextNonSkippedIndex(currentIndex + 1);
  };

  // Forward navigation applying jump rules and maintaining history.
  const goToNextAccordingToRules = (override = {}) => {
    const currentIndex = activeStep;
    if (currentIndex == null || currentIndex < 0) return;

    // Ensure history is initialized with current index
    let history = navigationHistory.length
      ? [...navigationHistory]
      : [currentIndex];

    // Find last occurrence of currentIndex in history and trim anything after it
    const lastPos = history.lastIndexOf(currentIndex);
    if (lastPos !== -1 && lastPos < history.length - 1) {
      history = history.slice(0, lastPos + 1);
    } else if (lastPos === -1) {
      // If currentIndex isn't present, append it
      history.push(currentIndex);
    }

    // Compute next index via hardcoded mapping (with fallback), honoring override
    const nextIndex = resolveJumpTarget(currentIndex, override);

    if (nextIndex == null) {
      // End of questionnaire
      fetch(process.env.REACT_APP_API_URI + "/v3/user/" + id + "/completed", {
        method: "PUT",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hash: id }),
      })
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          if (data.status === "success") {
            setIsLoading(false);
            navigate(`/thank-you`);
          } else {
            setIsLoading(false);
            console.error("Failed to mark assessment as completed...", data);
          }
        })
        .catch((err) => {
          console.error("Failed to mark assessment as completed...", err);
          setIsLoading(false);
        });

      return;
    }

    // If this question controls a branching mapping, record the chosen target so we can skip other branches
    const currentQ = questions[currentIndex];
    const mapping = HARD_CODED_JUMPS[currentQ?.id];
    if (mapping) {
      const chosenTargetId = questions[nextIndex]?.id;
      const mappingTargets = Object.values(mapping);
      if (mappingTargets.includes(chosenTargetId)) {
        setPathChoices((prev) => ({
          ...prev,
          [String(currentQ.id)]: chosenTargetId,
        }));
      }
    }

    // Update history and move
    history.push(nextIndex);
    setNavigationHistory(history);
    setActiveStep(nextIndex);
  };

  const getQuestions = useCallback(() => {
    fetch(
      process.env.REACT_APP_API_URI +
        "/v3/user/assessment/" +
        id +
        "/pages/" +
        pageId,
      {
        method: "GET",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
      }
    )
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.status === "success") {
          // Always sort questions to ensure indices are stable
          const sortedQuestions = (data?.data?.questions || [])
            .slice()
            .sort((a, b) => a.orderNo - b.orderNo);

          let goingForward = localStorage.getItem("goingForward") === "true";

          // Apply initial state
          setSections(data?.data?.sections || []);
          setQuestions(sortedQuestions);
          setQuestionCount(sortedQuestions.length);

          if (goingForward) {
            setActiveStep(0);
            setNavigationHistory([0]);
            localStorage.removeItem("goingForward");
          } else {
            // Rebuild path from answers to respect jump rules after reload
            const { path, current } = buildPathFromAnswers(sortedQuestions);
            setActiveStep(current);
            setNavigationHistory(path);

            // Seed pathChoices from first branching question in the path (parent -> chosen target)
            const newChoices = {};
            for (let i = 0; i < path.length - 1; i++) {
              const parentIdx = path[i];
              const nextIdx = path[i + 1];
              const parentId = sortedQuestions[parentIdx]?.id;
              if (parentId && HARD_CODED_JUMPS[parentId]) {
                newChoices[String(parentId)] = sortedQuestions[nextIdx]?.id;
              }
            }
            if (Object.keys(newChoices).length) {
              // set after questions state is set
              setTimeout(() => setPathChoices(newChoices), 0);
            }
          }

          setIsLoading(false);
        }
      })
      .catch((err) => console.error("Error while fetching questions...", err));
  }, [id, pageId]);

  useEffect(() => {
    setIsLoading(true);
    getQuestions();
    localStorage.removeItem("backFromDetailsPage");
  }, [pageId, getQuestions]);

  useEffect(() => {
    if (
      localStorage.getItem("backFromAddQPage") === "true" &&
      questions?.length
    ) {
      setActiveStep(questions?.length - 1);
      localStorage.removeItem("backFromAddQPage");
    }
  }, [questions, pageId]);

  const currentSectionName = sections?.find(
    (section) => section.id === questions?.[activeStep]?.sectionId
  )?.section;

  const prevQuestion = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // remove current
      const previousIndex = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setActiveStep(previousIndex);
    } else if (navigationHistory.length === 1) {
      // Single item in history: go back to where we came from
      navigateBackwardFunc();
    } else {
      // History not initialized, fallback to sequential back if possible
      if (activeStep > 0) setActiveStep(activeStep - 1);
      else navigateBackwardFunc();
    }
  };

  const nextQuestion = () => {
    const answerType = questions?.[activeStep]?.answerType;
    const numericAnswer = questions?.[activeStep]?.answer;
    const textAnswer = questions?.[activeStep]?.answerTxt;

    // Optional types are always considered answerable for navigation
    const isAnswered =
      NON_MANDATORY_TYPES.includes(answerType) ||
      (!NON_MANDATORY_TYPES.includes(answerType) && numericAnswer > 0);

    // Only submit textarea if user actually entered something non-empty
    if (answerType === "textarea") {
      const hasNonEmptyText =
        typeof textAnswer === "string" && textAnswer.trim().length > 0;
      if (hasNonEmptyText) submitAnswer(null, textAnswer);
    } else if (
      answerType === "checkbox" &&
      questions?.[activeStep]?.answers?.length > 0
    ) {
      submitAnswer(questions?.[activeStep]?.answers, null);
    }
    if (!isAnswered) return;

    // Use the same navigation logic with fresh override values
    goToNextAccordingToRules({ answer: numericAnswer, answerTxt: textAnswer });
  };

  const submitAnswer = (answer, answerTxt) => {
    const submittedStep = activeStep;
    const submittedQuestionId = questions?.[submittedStep]?.id;

    const updated = [...questions];
    // Find by id to be safe if the user moved meanwhile
    const idx = updated.findIndex((q) => q.id === submittedQuestionId);
    if (idx !== -1) {
      updated[idx] = { ...updated[idx], answer, answerTxt };
    }
    setQuestions(updated);

    // Only auto-advance if the user is still on the same step that submitted
    if (activeStep === submittedStep) {
      goToNextAccordingToRules({ answer, answerTxt });
    }

    let url = process.env.REACT_APP_API_URI + "/v3/user/assessment/" + id;
    let jsonBody = {};
    if(questions?.[activeStep]?.answerType === "checkbox") {
      jsonBody = {
        // "id": questions?.[activeStep]?.id,
        "pageId": pageId,
        // "answer": answer,
        "answer": questions?.[activeStep]?.answer,
        "answers": questions?.[activeStep]?.answers,
        "answerTxt": questions?.[activeStep]?.answers.filter(item => item.checked).map(item => item.desktopText || item.desktop_text).join(', '),
        "answerChecked": true,
      }
      console.log("jsonBody", jsonBody);
      url = process.env.REACT_APP_API_URI + "/v3/user/assessment/" + id + "/question/" + questions?.[activeStep]?.id;
    } else {
      jsonBody = {
        questions: [
          {
            id: submittedQuestionId,
            answer: answer,
            answers: questions?.[activeStep]?.answers,
            answerTxt: answerTxt,
          },
        ],
      };

    }
    
    fetch(url, {
      method: "PUT",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(jsonBody),
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.status === "success") {
          // Update local answer record
        }
      })
      .catch((err) => console.error("Failed to send answer details...", err));
  };

  const navigateBackwardFunc = () => {
    navigate("/?" + searchParams, { replace: true });
  };

  const moveToNextQuestion = (answer) => {
    // setTimeout(() => {
    //   submitAnswer(answer);
    // }, 200);
    submitAnswer(answer);
  };

  const nextDisabled = () => {
    const type = questions?.[activeStep]?.answerType;
    if (
      NON_MANDATORY_TYPES.includes(type) ||
      (type === "checkbox" &&
        questions?.[activeStep]?.checkboxValues?.length > 0)
    ) {
      return { display: "flex" };
    }
    if (questions?.[activeStep]?.answer > 0) {
      return { display: "flex" };
    }
    return { display: "none" };
  };

  const updateTextAnswer = (id, textVal) => {
    setQuestions(
      questions?.map((q) => {
        if (q.id === id) q.answerTxt = textVal;
        return q;
      })
    );
  };

  const updateCheckboxQuestion = (id, checkboxAnswers) => {
    let value = 0;
    const notaOption = checkboxAnswers?.find(
      (item) => item?.desktopText?.toLowerCase()?.trim() === "none of the above"
    );
    if (notaOption && notaOption.checked) {
      value = 0;
    } else {
      const lengthOfCheckedOptions = checkboxAnswers?.filter(
        (option) => option.checked
      )?.length;
      value = lengthOfCheckedOptions;
    }
    setQuestions(
      questions?.map((q) => {
        if (q.id === id) {
          q.answers = checkboxAnswers;
          q.answer = value;
        }
        return q;
      })
    );
  };

  // Old per-section utility kept for reference; replaced by visible-section counts below
  // const getQuestionsPerSection = () => {
  //   let arr = questions?.map((q) => q.sectionId);
  //   let count = {};
  //   arr?.forEach((element) => {
  //     count[element] = (count[element] || 0) + 1;
  //   });
  //   return Object.values(count);
  // };

  // Derive the actually visible questions based on current branch choices
  const getVisibleQuestions = () => {
    return (questions || []).filter((q) => !isSkippedQuestionId(q.id));
  };

  const visibleQuestions = getVisibleQuestions();
  const visibleCount = visibleQuestions.length;
  const visibleQuestionsPerSection = (() => {
    const sectionCounts = {};
    visibleQuestions.forEach((q) => {
      sectionCounts[q.sectionId] = (sectionCounts[q.sectionId] || 0) + 1;
    });
    return Object.values(sectionCounts);
  })();

  // Current progress index within the visible path (0-based)
  const currentVisibleIndex = (() => {
    const currentId = questions?.[activeStep]?.id;
    const idx = visibleQuestions.findIndex((q) => q.id === currentId);
    return idx >= 0 ? idx : activeStep;
  })();

  const getStepContent = (step) => {
    return (
      <Question
        question={questions?.[step]}
        questionCount={questionCount}
        questionAnswered={moveToNextQuestion}
        updateCheckboxQuestion={updateCheckboxQuestion}
        currentValue={questions?.[step]?.answer}
        updateTextAnswer={updateTextAnswer}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="container assessment loading">
        <CircularProgress style={{ color: "#c8a21b" }} size={60} />
      </div>
    );
  }

  return (
    <>
      <div className="container assessment">
        <div className="section-progress-top">
          <ProgressBar
            currentProgress={currentVisibleIndex}
            questionsPerSection={visibleQuestionsPerSection}
            sections={sections}
            count={visibleCount}
            currentSectionName={currentSectionName}
          />
        </div>
        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={activeStep}></Stepper>
        </Box>
        <div>
          <div className="steps-container">
            <div>
              <div className="steps">
                {windowWidth > 830 && <div className="spacer"></div>}

                <div className="progress-indicator">
                  <strong>{(currentVisibleIndex ?? 0) + 1}</strong>/
                  {visibleCount}
                </div>

                {getStepContent(activeStep)}

                <div className="step-buttons">
                  {windowWidth > 550 && (
                    <Button onClick={prevQuestion}>
                      {" "}
                      <img src={prevArrow} alt="previous button" />{" "}
                      &nbsp;&nbsp;PREV
                    </Button>
                  )}
                  {windowWidth <= 550 && (
                    <Button onClick={prevQuestion}>
                      {" "}
                      <img src={prevArrow} alt="previous button" /> &nbsp;&nbsp;
                    </Button>
                  )}
                  {windowWidth > 550 && (
                    <Button
                      className="next-btn"
                      onClick={nextQuestion}
                      style={nextDisabled()}
                    >
                      <span style={{ borderBottom: "1px solid #F6C415" }}>
                        {" "}
                        NEXT{" "}
                      </span>
                      <img
                        style={{ paddingLeft: "8px" }}
                        src={nextArrow}
                        alt="next button"
                      />
                    </Button>
                  )}
                  {windowWidth <= 550 && (
                    <Button
                      className="next-btn"
                      onClick={nextQuestion}
                      style={nextDisabled()}
                    >
                      <img
                        style={{ paddingLeft: "8px" }}
                        src={nextArrow}
                        alt="next button"
                      />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssessmentV3;
